import { createApp, reactive, watch } from 'vue';
import {
  detectAimetaPlatform,
  isAimetaAndroidWebView,
  isDesktopShell,
  shouldUseMobileLayout,
  shouldUseWebLoginOnMobile,
  isAimetaWindowsClient,
} from '@/utils/platform';
import {
  applyAimetaSkin,
  installAimetaSkinStorageSync,
  resolveAppliedAimetaSkin,
} from '@/hooks/useAimetaSkin';
import {
  syncNativeLoginFromWindow,
  enforceWebLoginFromNative,
  setupNativeLoginSyncListener,
  scheduleNativeLoginSyncRetry,
} from '@/utils/nativeLoginSync';
import {
  stripNativeDebugOverlay,
  watchAndStripNativeDebugOverlay,
} from '@/utils/stripNativeDebugOverlay';
import { registerReleaseBlockingOverlaysHook } from '@/utils/releaseBlockingOverlays';

const isMobile = shouldUseMobileLayout();

if (typeof document !== 'undefined') {
  document.documentElement.dataset.platform = detectAimetaPlatform();
  if (isMobile) {
    document.documentElement.classList.add('is-mobile');
  } else if (isDesktopShell()) {
    document.documentElement.classList.add('is-desktop-shell');
  }
  // 系统 priv-app 仍可能注入 debug_overlay.js；尽早阻断，避免「D」闪现
  if (isAimetaAndroidWebView()) {
    stripNativeDebugOverlay();
    watchAndStripNativeDebugOverlay();
  }
}
import { createPinia } from 'pinia';
import { useUserStore } from '@/stores/user';
import ElementPlus from 'element-plus'; //全局引入
import 'element-plus/dist/index.css';
import * as ElementPlusIconsVue from '@element-plus/icons-vue';
import App from './App.vue';
import router from './router';
import '@fontsource/nanum-gothic/korean-400.css';
import '@fontsource/nanum-gothic/korean-700.css';
import './assets/style/index.scss';
import './assets/main.css';
import '@/assets/iconfont/iconfont.css'; //引入阿里图标库
import { Swiper, SwiperSlide } from 'swiper/vue';
import IconFont from '@/components/common/IconFont.vue';
import IconSymbol from '@/components/common/IconSymbol.vue';
import UiButton from '@/components/button/UiButton.vue';
import NormalButton from '@/components/button/NormalButton.vue';
import CardBox from '@/components/common/CardBox.vue';
import CustomPopup from '@/components/common/CustomPopup.vue';
import CustomSearch from '@/components/common/CustomSearch.vue';
import CustomTip from '@/components/common/CustomTip.vue';
import CustomInput from '@/components/common/CustomInput.vue';
import CustomPopover from '@/components/common/CustomPopover.vue';
import CustomPicker from '@/components/common/CustomPicker.vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import SelectIcon from '@/components/common/SelectIcon.vue';
import CustomSwitch from '@/components/common/CustomSwitch.vue';
import CustomRadio from '@/components/common/CustomRadio.vue';
import CustomSelect from '@/components/common/CustomSelect.vue';
import SelectActive from '@/components/common/SelectActive.vue';
import CustomTabsIcon from '@/components/common/CustomTabsIcon.vue';

import message from '@/utils/messageManager';
import clickOutside from '@/utils/directives/click-outside'; // 导入自定义指令
import NumberOnlyDirective from '@/utils/directives/number-only';
import methods, {
  globalMethodsPlugin,
  mergeAppConfigWithDefaults,
} from '@/utils/localMethod';
import {
  createI18nInstance,
  loadLanguageAsync,
  getCurrentLocale,
  languages as i18nLanguages,
  syncDocumentLocaleFromCode,
  LANGUAGE_KEY,
} from './i18n';

import { formatFileSize } from './utils/formatFileSize';
import { formatTime } from '@/utils/formatTime';

const defaultAppConfig = {
  defaultLanguage: 'zh',
  product: true,
  robot: false,
  scheduledShutdown: false,
  washManuscript: true,
  promotionScript: true,
  /** 直播间侧栏「定时引导」模块；checkpoint 可写 timedGuidance: false 关闭 */
  timedGuidance: true,
  scriptSettings: {
    freedom: true,
    fixed: true,
  },
  speechEngines: ['basic', 'premium', 'pro', 'cloud'],
  roleCategories: ['standard', 'advanced'],
  liveType: [
    'douyin',
    'xiaohongshu',
    'kuaishou',
    'wechat',
    'taobao',
    'pinduoduo',
    'jd',
    'tiktok',
    'douyu',
    'bilibili',
    'huya',
    'pino',
  ],
  /** 与 system_param.accountMode 一致；checkpoint 未就绪前默认 phone，避免注册页误判为邮箱分支 */
  accountMode: 'phone' as 'email' | 'phone',
  /** 系统参数 freeScriptMinChars（默认 30）；自由话术按条成段后前端不再用其做最短校验 */
  freeScriptMinChars: 30,
  /** 系统参数 freeScriptMaxChars（默认 300） */
  freeScriptMaxChars: 300,
  /** 知识库是否允许导入 Word（system_param.knowledgeAllowImportWord，默认允许） */
  knowledgeAllowImportWord: true,
  /** 角色是否上传到云端（system_param.roleUploadToCloud，默认关闭） */
  roleUploadToCloud: false,
};

const appConfig: any = reactive(
  mergeAppConfigWithDefaults(defaultAppConfig, {
    ...(await methods.getLocalConfig().catch(() => ({}))),
  })
);

// Windows 壳下确保必要功能不被 checkpoint 异常覆盖
if (isAimetaWindowsClient()) {
  if (appConfig.washManuscript !== true) appConfig.washManuscript = true;
  if (appConfig.promotionScript !== true) appConfig.promotionScript = true;
  if (!appConfig.scriptSettings || typeof appConfig.scriptSettings !== 'object') {
    appConfig.scriptSettings = { freedom: true, fixed: true };
  }
  if (appConfig.scriptSettings.freedom !== true) appConfig.scriptSettings.freedom = true;
  if (appConfig.scriptSettings.fixed !== true) appConfig.scriptSettings.fixed = true;
}

async function mergeCheckpointIntoAppConfig() {
  try {
    const partial = await methods.getLocalConfig();
    const merged = mergeAppConfigWithDefaults(defaultAppConfig, partial);
    Object.assign(appConfig, merged);
    applySkinFromConfig(appConfig?.skin);
  } catch {
    /* ignore */
  }
}

const applySkinFromConfig = (skin: string | undefined) => {
  if (isMobile) {
    localStorage.setItem('aimeta_skin', 'tech');
    document.documentElement.setAttribute('data-skin', 'tech');
    return;
  }
  applyAimetaSkin(resolveAppliedAimetaSkin(skin));
};

applySkinFromConfig(appConfig?.skin);
installAimetaSkinStorageSync();

const app = createApp(App);

app.provide('appConfig', appConfig);
app.provide('mergeCheckpointIntoAppConfig', mergeCheckpointIntoAppConfig);

const i18n = await createI18nInstance(appConfig?.defaultLanguage);

watch(
  () => String((i18n.global.locale as any).value ?? 'zh'),
  (code) => syncDocumentLocaleFromCode(code),
  { immediate: true }
);

app.use(i18n);

declare global {
  interface Window {
    __AIMETA_RELOAD_LOCALE?: (lang?: string) => Promise<void>;
  }
}

window.__AIMETA_RELOAD_LOCALE = async (lang?: string) => {
  const code =
    lang && i18nLanguages.some((l) => l.code === lang)
      ? lang
      : getCurrentLocale();
  await loadLanguageAsync(code, { force: true });
};

// 全局禁用右键菜单
document.addEventListener('contextmenu', (e) => {
  e.preventDefault();
});

// 全局注册组件
app.component('IconFont', IconFont);
app.component('IconSymbol', IconSymbol);
app.component('UiButton', UiButton);
app.component('NormalButton', NormalButton);
app.component('CardBox', CardBox);
app.component('CustomPopup', CustomPopup);
app.component('CustomSearch', CustomSearch);
app.component('CustomTip', CustomTip);
app.component('CustomInput', CustomInput);
app.component('CustomPopover', CustomPopover);
app.component('CustomPicker', CustomPicker);
app.component('CustomTooltip', CustomTooltip);
app.component('SelectIcon', SelectIcon);
app.component('CustomSwitch', CustomSwitch);
app.component('CustomRadio', CustomRadio);
app.component('CustomSelect', CustomSelect);
app.component('SelectActive', SelectActive);
app.component('Swiper', Swiper);
app.component('SwiperSlide', SwiperSlide);
app.component('CustomTabsIcon', CustomTabsIcon);

// 注册全局指令
app.directive('click-outside', clickOutside); //点击外部隐藏元素
app.directive('number-only', NumberOnlyDirective); // 只允许输入数字

// 注册全局方法
app.use(globalMethodsPlugin);

app.config.globalProperties.$message = message;
app.config.globalProperties.$formatFileSize = formatFileSize;
app.config.globalProperties.$formatTime = formatTime;

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component);
}

app.use(createPinia());

// 仅手机 App WebView：从系统 API 注入的登录态同步到 Pinia / localStorage
if (isAimetaAndroidWebView()) {
  syncNativeLoginFromWindow();
  setupNativeLoginSyncListener();
  scheduleNativeLoginSyncRetry();
  registerReleaseBlockingOverlaysHook();
}

app.use(router);

if (isAimetaAndroidWebView()) {
  window.addEventListener('aimeta:native-login-ready', () => {
    if (shouldUseWebLoginOnMobile()) {
      const authed = enforceWebLoginFromNative();
      const path = router.currentRoute.value.path;
      if (!authed && path !== '/' && path !== '/resetPassword') {
        router.replace('/');
      } else if (authed && (path === '/' || path === '')) {
        router.replace('/home/');
      }
      return;
    }
    const userStore = useUserStore();
    const path = router.currentRoute.value.path;
    if (userStore.token && (path === '/' || path === '')) {
      router.replace('/home/');
    }
  });
}
app.use(ElementPlus);
app.mount('#app');
